import { observer } from 'mobx-react-lite';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import DateSelector from 'owa-calendar-dateselector/lib/components/DateSelector';
import { preventFocusDuringCurrentRender } from 'owa-calendar-focus-manager';
import type { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { userDate } from 'owa-datetime';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import * as React from 'react';
import {
    updateDateTimes,
    logStartTimeEndTimeChangedCount,
} from 'owa-calendar-compose-form-actions';
import { getComposeEvent, getComposeViewState } from 'owa-calendar-compose-store-manager';
import { lazyIsFullComposeOpen } from 'owa-calendar-compose-form-store';
import { isFeatureEnabled } from 'owa-feature-flags';

export interface SmartDateSelectorProps {
    isNarrow: boolean;
    showDateTimeOnly?: boolean;
    showTimeZones?: boolean;
    className?: string;
    datePickerClassName?: string;
    timeChangeActionType: TimeChangeActionType;
    onSizeUpdate?: () => void;
    onDateTimesChanged?: () => void;
    dateRowClassName?: string;
    startEndBlockClassName?: string;
    timeAndDurationColumnClassName?: string;
    timePickerClassName?: string;
    durationDropDownClassName?: string;
    timePickerMultilineClassName?: string;
    disabled?: boolean;
    renderAfterDateRow?: (
        timePickerRefTarget: HTMLElement | React.RefObject<Element>
    ) => JSX.Element;
    formId?: string | null;
}

export default observer(function SmartDateSelector(props: SmartDateSelectorProps) {
    const onDateTimesChanged = (datesSelected: OwaDateRange) => {
        if (props.onDateTimesChanged) {
            props.onDateTimesChanged();
        }
        updateDateTimes(datesSelected, props.timeChangeActionType);
    };
    let {
        isNarrow,
        className,
        datePickerClassName,
        dateRowClassName,
        startEndBlockClassName,
        showTimeZones,
        onSizeUpdate,
        showDateTimeOnly,
        timeAndDurationColumnClassName,
        timePickerClassName,
        durationDropDownClassName,
        timePickerMultilineClassName,
        disabled,
        renderAfterDateRow,
        formId,
    } = props;

    const colorScheme: ColorScheme = getComposeCalendarColorScheme(formId);
    const event = getComposeEvent(formId);
    const uiState = getComposeViewState(formId);
    const start = showTimeZones ? event.Start : userDate(event.Start);
    const end = showTimeZones ? event.End : userDate(event.End);
    const isFullComposeOpen = lazyIsFullComposeOpen.tryImportForRender()?.() ?? false;
    const preferEndDateToBeShown = isFullComposeOpen && isFeatureEnabled('mon-cal-dateTimeInEdit');
    return (
        <DateSelector
            colorScheme={colorScheme}
            start={start}
            end={end}
            isAllDay={event.IsAllDayEvent}
            isNarrow={isNarrow}
            showTimeZones={showTimeZones}
            showDateTimeOnly={showDateTimeOnly}
            onDateTimesChanged={onDateTimesChanged}
            onBeforeTimeZoneSelect={preventFocusDuringCurrentRender}
            className={className}
            datePickerClassName={datePickerClassName}
            dateRowClassName={dateRowClassName}
            startEndBlockClassName={startEndBlockClassName}
            onSizeUpdate={onSizeUpdate}
            timeAndDurationColumnClassName={timeAndDurationColumnClassName}
            timePickerClassName={timePickerClassName}
            durationDropDownClassName={durationDropDownClassName}
            timePickerMultilineClassName={timePickerMultilineClassName}
            disabled={disabled}
            isEndTimePicker={true}
            isStartTimePicker={true}
            shouldAdjustStartTimeEndTime={uiState.shouldAdjustStartTimeEndTime}
            logStartTimeEndTimeChangedCount={logStartTimeEndTimeChangedCount}
            renderAfterDateRow={renderAfterDateRow}
            preferEndDateToBeShown={preferEndDateToBeShown}
        />
    );
});
